import React, { useEffect, useState } from "react";
import {
  X, Trash2, ChevronDown, ChevronLeft,
  Upload, Camera, Video, AlertCircle, Save,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { RootState, AppDispatch } from "../../redux/store";
import {
  upsertProduct,
  upsertProductMedia,
  deleteProductPhoto,
  listenToProductData,
} from "../../redux/productSlice";
import { listenToUserCommercePartners } from "../../redux/commercePartnersSlice";
import {
  electronicsColorAttributes,
  electronicsAttributes,
  ElectronicsAttributeGroup,
} from "../../UNIVERSAL/ATTRIBUTES/ElectronicsAttributes";
import {
  electronicsCategories,
  electronicsSubcategories,
  electronicsProducts,
} from "../../UNIVERSAL/CATEGORIES/ElectronicsCategories";

/* ─────────────────────────── tokens ─────────────────────────── */
const T = {
  pageBg:   "#08060F",
  cardBg:   "#100D1C",
  surface:  "#1A1530",
  surface2: "#0D0A1A",
  grad:     "linear-gradient(90deg,#007BFF 0%,#6A00FF 50%,#FF00D4 100%)",
  gradAnim: "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4,#6A00FF,#007BFF)",
  blue:     "#007BFF",
  purple:   "#6A00FF",
  pink:     "#FF00D4",
  white:    "#FFFFFF",
  offwhite: "#E8E4FF",
  muted:    "#8B82A8",
  mutedDim: "#5A5278",
  border:   "rgba(106,0,255,0.22)",
  gold:     "#FFD60A",
  red:      "#FF3B5C",
  green:    "#00E676",
};
const GRAD = T.grad;
const GA   = T.gradAnim;

/* ─────────────────────────── CSS ─────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  input,button,select,textarea{font-family:inherit;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes spin{to{transform:rotate(360deg);}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes shimmer{0%{background-position:-200% center;}100%{background-position:200% center;}}

  input::placeholder,textarea::placeholder{color:rgba(255,255,255,.22);}
  select option{background:#100D1C;color:#E8E4FF;}

  /* ── neon focus ── */
  .ni:focus{outline:none;border-color:rgba(106,0,255,.7)!important;box-shadow:0 0 0 3px rgba(106,0,255,.15);}
  textarea.ni:focus{outline:none;border-color:rgba(106,0,255,.7)!important;box-shadow:0 0 0 3px rgba(106,0,255,.15);}

  /* ── section card ── */
  .sc{
    background:#100D1C;
    border:1.5px solid rgba(106,0,255,.20);
    border-radius:20px;
    padding:22px;
    margin-bottom:16px;
    animation:fadeUp .35s ease both;
  }

  /* ── desktop: 2-col grid ── */
  @media(min-width:768px){
    .form-grid{
      display:grid;
      grid-template-columns:1fr 1fr;
      gap:16px;
      align-items:start;
    }
    .sc-full{grid-column:1/-1;}
    .sc{margin-bottom:0;}
  }

  /* ── attribute chip ── */
  .ac{
    padding:6px 14px;border-radius:999px;font-size:12px;font-weight:700;
    cursor:pointer;border:1.5px solid rgba(106,0,255,.25);
    background:rgba(106,0,255,.08);color:#8B82A8;transition:all .15s;
  }
  .ac:hover{border-color:rgba(106,0,255,.6);color:#E8E4FF;background:rgba(106,0,255,.15);}
  .ac.on{
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);
    background-size:200% 100%;animation:gradSpin 3s ease infinite;
    border-color:transparent;color:#fff;box-shadow:0 2px 10px rgba(106,0,255,.4);
  }

  /* ── toggle ── */
  .ttrack{width:46px;height:26px;border-radius:13px;border:none;cursor:pointer;position:relative;transition:background .22s;flex-shrink:0;}
  .tthumb{position:absolute;top:3px;width:20px;height:20px;border-radius:50%;background:#fff;transition:left .22s;box-shadow:0 1px 4px rgba(0,0,0,.4);}

  /* ── media thumb ── */
  .media-thumb{
    width:80px;height:96px;position:relative;overflow:hidden;
    border-radius:14px;border:1.5px solid rgba(106,0,255,.25);
    transition:transform .2s,box-shadow .2s;
  }
  .media-thumb:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(106,0,255,.3);}

  /* ── add media label ── */
  .add-media{
    width:80px;height:96px;border:1.5px dashed rgba(106,0,255,.4);
    border-radius:14px;background:rgba(106,0,255,.06);
    display:flex;flex-direction:column;align-items:center;justify-content:center;
    cursor:pointer;gap:6px;transition:border-color .2s,background .2s;
  }
  .add-media:hover{border-color:rgba(106,0,255,.7);background:rgba(106,0,255,.12);}

  /* ── section label shimmer ── */
  .sec-label{
    font-size:10px;font-weight:800;letter-spacing:.22em;
    background:linear-gradient(90deg,#007BFF 0%,#E8E4FF 40%,#FF00D4 70%,#007BFF 100%);
    background-size:200% auto;
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
    animation:shimmer 4s linear infinite;
    margin-bottom:4px;
  }

  /* ── colour swatch ── */
  .sw{
    width:30px;height:30px;border-radius:50%;cursor:pointer;
    transition:transform .15s,box-shadow .15s;
  }
  .sw:hover{transform:scale(1.15);}

  /* ── visibility row ── */
  .vis-row{
    display:flex;align-items:center;justify-content:space-between;
    padding:14px 0;border-bottom:1px solid rgba(106,0,255,.12);
  }
  .vis-row:last-child{border-bottom:none;}
`;

/* ─────────────────────── category groups ──────────────────────── */
const CATEGORY_GROUPS = [
  { name: "Electronics", categories: electronicsCategories, subs: electronicsSubcategories, products: electronicsProducts },
];
const MAX_IMAGES = 5;

/* ───────────────────────── form type ──────────────────────────── */
interface FormState {
  listingId?:    string;
  groupIndex:    number | null;
  categoryId:    number | null;
  subcategoryId: number | null;
  productId:     number | null;
  title:         string;
  description:   string;
  stock:         number;
  rate:          number;
  available:     boolean;
  isPublished:   boolean;
  images:        File[];
  photos:        string[];
  video?:        string | null;
  videoFile?:    File | null;
  attributes:    Record<number, number | number[] | undefined>;
  variants:      Record<number, { imageUrl?: string; imageFile?: File; previewUrl?: string; sizeIds: number[]; stock: number }>;
}

const blank = (): FormState => ({
  groupIndex: null, categoryId: null, subcategoryId: null, productId: null,
  title: "", description: "", stock: 0, rate: 0, available: true, isPublished: false,
  images: [], photos: [], video: null, videoFile: null, attributes: {}, variants: {},
});

const findGroup = (catId: number | null) => catId == null
  ? null
  : CATEGORY_GROUPS.findIndex(g => g.categories.some((c: any) => c.id === catId));

/* ─────────────────── tiny UI primitives ───────────────────────── */

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button className="ttrack" onClick={() => onChange(!on)}
    style={{ background: on ? GRAD : "rgba(255,255,255,.1)", backgroundSize: "200% 100%", animation: on ? "gradSpin 3s ease infinite" : "none" }}>
    <div className="tthumb" style={{ left: on ? 23 : 3 }} />
  </button>
);

const SLabel: React.FC<{ t: string; sub?: string }> = ({ t, sub }) => (
  <div style={{ marginBottom: 14 }}>
    <div className="sec-label">{t.toUpperCase()}</div>
    {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>{sub}</div>}
  </div>
);

const FL: React.FC<{ l: string; req?: boolean }> = ({ l, req }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", color: T.muted, marginBottom: 6 }}>
    {l.toUpperCase()}{req && <span style={{ color: T.pink, marginLeft: 3 }}>✦</span>}
  </div>
);

const NIn: React.FC<{ value: string|number; onChange:(v:string)=>void; placeholder?:string; type?:string; rows?:number; prefix?:string; disabled?:boolean }> = ({ value, onChange, placeholder, type="text", rows, prefix, disabled }) => (
  <div style={{ display:"flex", border:`1.5px solid rgba(106,0,255,.22)`, borderRadius:12, background: disabled ? "rgba(255,255,255,.03)" : T.surface2, overflow:"hidden", opacity: disabled ? 0.5 : 1 }}>
    {prefix && <div style={{ padding:"0 13px", display:"flex", alignItems:"center", background:"rgba(106,0,255,.1)", borderRight:`1px solid rgba(106,0,255,.2)`, fontSize:13, color:T.purple, fontWeight:700 }}>{prefix}</div>}
    {rows
      ? <textarea className="ni" value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled}
          style={{ flex:1, border:"none", outline:"none", padding:"11px 14px", fontSize:13, color:T.offwhite, background:"transparent", resize:"vertical", lineHeight:1.6 }} />
      : <input className="ni" value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} type={type} disabled={disabled}
          style={{ flex:1, border:"none", outline:"none", padding:"11px 14px", fontSize:13, color:T.offwhite, background:"transparent" }} />
    }
  </div>
);

const NSel: React.FC<{ value:number|string; onChange:(v:number)=>void; options:{id:number;name:string}[]; placeholder?:string; disabled?:boolean }> = ({ value, onChange, options, placeholder="Select…", disabled }) => (
  <div style={{ position:"relative" }}>
    <select value={value??""} onChange={e=>onChange(+e.target.value)} disabled={disabled} className="ni"
      style={{ width:"100%", border:`1.5px solid rgba(106,0,255,.22)`, borderRadius:12, background: disabled ? "rgba(255,255,255,.03)" : T.surface2, color: value ? T.offwhite : T.muted, padding:"11px 40px 11px 14px", fontSize:13, outline:"none", appearance:"none", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }}>
      <option value="" disabled>{placeholder}</option>
      {options.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
    <ChevronDown size={14} color={T.muted} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }} />
  </div>
);

const Toast: React.FC<{ visible:boolean; msg:string; type:"ok"|"err"|"warn" }> = ({ visible, msg, type }) => (
  <AnimatePresence>
    {visible && (
      <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:20 }}
        style={{ position:"fixed", bottom:32, left:"50%", transform:"translateX(-50%)", background: type==="ok" ? GRAD : type==="err" ? "#8B0000" : "#7A5C00", backgroundSize: type==="ok" ? "200% 100%" : undefined, animation: type==="ok" ? "gradSpin 3s ease infinite" : undefined, color:"#fff", fontSize:12, fontWeight:700, letterSpacing:"0.1em", padding:"13px 28px", borderRadius:999, zIndex:9999, boxShadow:"0 8px 32px rgba(0,0,0,.4)", whiteSpace:"nowrap" }}>
        {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ─────────────────────── variant card ─────────────────────────── */
const VariantCard: React.FC<{
  colorId: number;
  variant: FormState["variants"][number] | undefined;
  stock:   number;
  onUpdate:(id:number,p:Partial<FormState["variants"][number]>)=>void;
  onRemove:(id:number)=>void;
}> = ({ colorId, variant, stock, onUpdate, onRemove }) => {
  const color = electronicsColorAttributes.values.find(v => v.id === colorId);
  return (
    <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
      style={{ border:`1.5px solid rgba(106,0,255,.22)`, borderRadius:14, background:T.surface, padding:14, marginBottom:10, position:"relative" }}>
      <button onClick={()=>onRemove(colorId)} style={{ position:"absolute", top:10, right:10, background:"none", border:"none", cursor:"pointer", color:T.muted }}>
        <X size={14} />
      </button>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
        {color?.hexCodes?.[0] && <div style={{ width:14, height:14, borderRadius:"50%", background:color.hexCodes[0], border:"1px solid rgba(255,255,255,.2)" }} />}
        <span style={{ fontSize:11, fontWeight:800, letterSpacing:"0.12em", color:T.offwhite }}>{color?.name?.toUpperCase()}</span>
      </div>
      <div style={{ display:"flex", gap:12 }}>
        <label style={{ width:84, height:100, borderRadius:12, background:T.surface2, border:`1.5px dashed rgba(106,0,255,.4)`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden", flexShrink:0, gap:5 }}>
          {variant?.previewUrl || variant?.imageUrl
            ? <img src={variant.previewUrl ?? variant.imageUrl} alt={color?.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
            : <><Camera size={18} color={T.purple}/><span style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em" }}>UPLOAD</span></>
          }
          <input type="file" hidden accept="image/*" onChange={e=>{ const f=e.target.files?.[0]; if(!f)return; onUpdate(colorId,{imageFile:f,previewUrl:URL.createObjectURL(f)}); }} />
        </label>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:9, letterSpacing:"0.16em", color:T.muted, marginBottom:6, fontWeight:700 }}>STOCK</div>
          <input type="number" value={variant?.stock ?? stock ?? 0} onChange={e=>onUpdate(colorId,{stock:+e.target.value})} className="ni"
            style={{ border:`1.5px solid rgba(106,0,255,.22)`, borderRadius:10, background:T.surface2, padding:"9px 12px", fontSize:13, width:"100%", outline:"none", color:T.offwhite }} />
        </div>
      </div>
    </motion.div>
  );
};

/* ══════════════════════ MAIN ══════════════════════════════════ */
const AddProducts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { listingId: pid } = useParams<{ listingId?: string }>();

  const userId         = useSelector((s: RootState) => s.auth.user?.userId);
  const partners       = useSelector((s: RootState) => s.commercePartners.partners);
  const partnerLoading = useSelector((s: RootState) => s.commercePartners.loading);
  const currentProduct = useSelector((s: RootState) => s.products.currentProduct);

  const [form,   setForm]   = useState<FormState>(blank());
  const [saving, setSaving] = useState(false);
  const [dirty,  setDirty]  = useState(false);
  const [snack,  setSnack]  = useState<{open:boolean;msg:string;type:"ok"|"err"|"warn"}>({open:false,msg:"",type:"ok"});
  const [isMob,  setIsMob]  = useState(window.innerWidth < 768);

  useEffect(()=>{ const fn=()=>setIsMob(window.innerWidth<768); window.addEventListener("resize",fn); return()=>window.removeEventListener("resize",fn); },[]);

  const toast = (msg:string,type:"ok"|"err"|"warn"="ok") => {
    setSnack({open:true,msg,type});
    setTimeout(()=>setSnack(s=>({...s,open:false})),3000);
  };

  useEffect(()=>{ if(userId) dispatch(listenToUserCommercePartners(userId) as any); },[userId,dispatch]);
  useEffect(()=>{ if(!userId||partnerLoading)return; if(partners.length===0) navigate(`/CommerceOnboarding/${userId}`,{replace:true}); },[partners,partnerLoading,userId,navigate]);
  useEffect(()=>{ if(pid) dispatch(listenToProductData(pid)); },[dispatch,pid]);

  useEffect(()=>{
    if(!pid||!currentProduct)return;
    setForm({
      listingId:     currentProduct.listingId,
      groupIndex:    findGroup(currentProduct.categoryId??null),
      categoryId:    currentProduct.categoryId??null,
      subcategoryId: (currentProduct as any).subcategoryId??null,
      productId:     currentProduct.productId??null,
      title:         currentProduct.name??"",
      description:   (currentProduct as any).description??"",
      stock:         currentProduct.stock??0,
      rate:          currentProduct.rate??0,
      available:     currentProduct.available??true,
      isPublished:   currentProduct.isPublished??false,
      images:[], photos: currentProduct.photos??[], video: currentProduct.videoUrl??null, videoFile:null,
      attributes:    (currentProduct as any).attributes??{},
      variants:      currentProduct.variants
        ? Object.fromEntries(Object.entries(currentProduct.variants).map(([k,v]:any)=>[Number(k),{imageUrl:v.imageUrl,imageFile:undefined,previewUrl:undefined,sizeIds:v.sizeIds??[],stock:v.stock??0}]))
        : {},
    });
    setDirty(false);
  },[currentProduct,pid]);

  const up = <K extends keyof FormState>(key:K,value:FormState[K]) => { setForm(p=>({...p,[key]:value})); setDirty(true); };

  const group = form.groupIndex!==null ? CATEGORY_GROUPS[form.groupIndex] : null;
  const subs  = group ? group.subs.filter((s:any)=>s.parentId===form.categoryId) : [];
  const prods = group ? group.products.filter((p:any)=>p.parentId===form.subcategoryId) : [];
  const isElec = form.groupIndex!==null && CATEGORY_GROUPS[form.groupIndex]?.name==="Electronics";
  const colorVal = form.attributes?.[electronicsColorAttributes.id];
  const selColors: number[] = Array.isArray(colorVal) ? colorVal : [];
  const hasVariants = isElec && selColors.length>0;

  const updateVariant = (colorId:number, patch:Partial<FormState["variants"][number]>) => {
    const ex = form.variants?.[colorId];
    up("variants",{...form.variants,[colorId]:{...(ex??{sizeIds:[],stock:0}),...patch}});
  };
  const removeVariantColor = (colorId:number) => {
    up("attributes",{...form.attributes,[electronicsColorAttributes.id]:selColors.filter(c=>c!==colorId)});
    const nv={...form.variants}; delete nv[colorId]; up("variants",nv);
  };
  const toggleColor = (colorId:number,checked:boolean) => {
    const nc=checked?[...selColors,colorId]:selColors.filter(c=>c!==colorId);
    up("attributes",{...form.attributes,[electronicsColorAttributes.id]:nc});
    if(checked&&!form.variants?.[colorId]) up("variants",{...form.variants,[colorId]:{sizeIds:[],stock:form.stock??0}});
    else if(!checked){ const nv={...form.variants}; delete nv[colorId]; up("variants",nv); }
  };

  const handleSave = async () => {
    if(!form.title.trim()){toast("Title is required","warn");return;}
    setSaving(true);
    const cleanAttr:Record<number,number|number[]>=Object.fromEntries(Object.entries(form.attributes??{}).filter(([,v])=>v!==undefined).map(([k,v])=>[k,v as number|number[]]));
    try {
      const isVB=selColors.length>0;
      const saved=await dispatch(upsertProduct({
        listingId:form.listingId,
        data:{
          name:form.title, description:form.description,
          categoryId:form.categoryId, subcategoryId:form.subcategoryId, productId:form.productId,
          rate:form.rate, available:form.available, isPublished:form.isPublished, isDraft:!form.isPublished,
          attributes:cleanAttr,
          ...(isVB
            ?{ variants:Object.fromEntries(Object.entries(form.variants||{}).filter(([,v])=>!!v.imageUrl).map(([k,v])=>[k,{colorId:+k,imageUrl:v.imageUrl!,sizeIds:v.sizeIds??[],stock:v.stock??0}])) }
            :{ stock:form.stock }),
        },
      })).unwrap();

      if(isVB&&form.variants){
        for(const [cid,vrt] of Object.entries(form.variants)){
          if(!vrt.imageFile)continue;
          const res=await dispatch(upsertProductMedia({listingId:saved.listingId,files:[vrt.imageFile],type:"photo"})).unwrap();
          const url=res.downloadURLs?.[0];
          if(url) updateVariant(Number(cid),{imageUrl:url,imageFile:undefined,previewUrl:url});
        }
        await dispatch(upsertProduct({listingId:saved.listingId,data:{variants:Object.fromEntries(Object.entries(form.variants).filter(([,v])=>!!v.imageUrl).map(([k,v])=>[k,{colorId:+k,imageUrl:v.imageUrl!,sizeIds:v.sizeIds??[],stock:v.stock??0}]))}})).unwrap();
      }
      if(form.images.length>0) await dispatch(upsertProductMedia({listingId:saved.listingId,files:form.images,type:"photo"})).unwrap();
      if(form.videoFile)        await dispatch(upsertProductMedia({listingId:saved.listingId,files:[form.videoFile],type:"video"})).unwrap();

      toast("Product saved ✓");
      setForm(p=>({...p,listingId:saved.listingId,images:[],videoFile:null}));
      setDirty(false);
    } catch(err){ console.error(err); toast("Save failed","err"); }
    finally{ setSaving(false); }
  };

  const handleDelPhoto=(url:string)=>{
    if(!form.listingId)return;
    dispatch(deleteProductPhoto({listingId:form.listingId,photoUrl:url}));
    up("photos",form.photos.filter(p=>p!==url));
  };

  const isEdit=!!pid;

  /* ── section wrapper ── */
  const Sec: React.FC<{children:React.ReactNode; full?:boolean; delay?:number}> = ({children,full,delay=0}) => (
    <div className={`sc${full?" sc-full":""}`} style={{ animationDelay:`${delay}ms` }}>{children}</div>
  );

  /* ═══════════ RENDER ═══════════ */
  return (
    <div style={{ minHeight:"100vh", background:T.pageBg, fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:80 }}>
      <style>{CSS}</style>

      {/* ── STICKY HEADER ── */}
      <div style={{ background:T.cardBg, borderBottom:`1px solid rgba(106,0,255,.18)`, position:"sticky", top:0, zIndex:100 }}>
        {/* animated top bar */}
        <div style={{ height:3, background:GA, backgroundSize:"300% 100%", animation:"gradSpin 4s linear infinite" }} />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"13px 20px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={()=>navigate(-1)} style={{ background:"rgba(106,0,255,.1)", border:`1px solid rgba(106,0,255,.3)`, borderRadius:10, cursor:"pointer", color:T.muted, display:"flex", alignItems:"center", justifyContent:"center", width:36, height:36 }}>
              <ChevronLeft size={20} />
            </button>
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.22em", color:T.mutedDim }}>{isEdit?"EDITING":"NEW PRODUCT"}</div>
              <h1 style={{ fontWeight:900, fontSize:isMob?18:22, color:T.white, letterSpacing:-0.3, lineHeight:1.1 }}>
                {isEdit ? form.title || "Edit Product" : "Add Product"}
              </h1>
            </div>
          </div>

          {/* header save */}
          <motion.button whileTap={{scale:0.93}} disabled={!dirty||saving} onClick={handleSave}
            style={{ display:"flex", alignItems:"center", gap:7, background: dirty&&!saving ? GRAD : "rgba(255,255,255,.06)", backgroundSize:"200% 100%", animation: dirty&&!saving ? "gradSpin 3s ease infinite" : "none", border:"none", borderRadius:999, padding:"10px 20px", color: dirty&&!saving ? "#fff" : T.mutedDim, fontWeight:800, fontSize:13, cursor: dirty&&!saving ? "pointer" : "not-allowed", boxShadow: dirty&&!saving ? "0 4px 20px rgba(106,0,255,.45)" : "none", fontFamily:"inherit", transition:"all .2s" }}>
            {saving
              ? <div style={{ width:15,height:15,border:"2px solid #fff",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite" }} />
              : <><Save size={14}/> Save</>
            }
          </motion.button>
        </div>
      </div>

      {/* ── UNSAVED BANNER ── */}
      <AnimatePresence>
        {dirty && (
          <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}}
            style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(255,214,10,.08)", border:"1px solid rgba(255,214,10,.25)", padding:"10px 20px" }}>
            <AlertCircle size={14} color={T.gold} />
            <span style={{ fontSize:12, color:T.gold, fontWeight:700 }}>Unsaved changes</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FORM ── */}
      <div style={{ maxWidth:isMob?480:1040, margin:"0 auto", padding: isMob ? "16px 14px" : "28px 32px" }}>
        <div className={isMob ? "" : "form-grid"}>

          {/* ══ CLASSIFICATION ══ */}
          <Sec delay={0}>
            <SLabel t="Classification" />

            <div style={{ marginBottom:12 }}>
              <FL l="Category Group" req /><NSel value={form.groupIndex??""} onChange={v=>{up("groupIndex",v);up("categoryId",null);up("subcategoryId",null);up("productId",null);}} options={CATEGORY_GROUPS.map((g,i)=>({id:i,name:g.name}))} placeholder="Select group…" />
            </div>
            <div style={{ marginBottom:12 }}>
              <FL l="Category" req /><NSel value={form.categoryId??""} onChange={v=>{up("categoryId",v);up("subcategoryId",null);up("productId",null);}} options={(group?.categories??[]) as any} placeholder="Select category…" disabled={!group} />
            </div>
            {subs.length>0 && (
              <div style={{ marginBottom:12 }}>
                <FL l="Subcategory" /><NSel value={form.subcategoryId??""} onChange={v=>{up("subcategoryId",v);up("productId",null);}} options={subs as any} placeholder="Select subcategory…" disabled={!form.categoryId||subs.length===0} />
              </div>
            )}
            {prods.length>0 && (
              <div>
                <FL l="Product Type" /><NSel value={form.productId??""} onChange={v=>up("productId",v)} options={prods as any} placeholder="Select product type…" />
              </div>
            )}
          </Sec>

          {/* ══ PRODUCT DETAILS ══ */}
          <Sec delay={60}>
            <SLabel t="Product Details" />
            <div style={{ marginBottom:12 }}>
              <FL l="Title" req /><NIn value={form.title} onChange={v=>up("title",v)} placeholder="e.g. Samsung Galaxy S25 Ultra" />
            </div>
            <div style={{ marginBottom:12 }}>
              <FL l="Description" /><NIn value={form.description} onChange={v=>up("description",v)} placeholder="Describe this product…" rows={isMob?3:4} />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div><FL l="Price" req /><NIn value={form.rate||""} onChange={v=>up("rate",parseFloat(v)||0)} prefix="€" type="number" /></div>
              <div><FL l="Stock" /><NIn value={form.stock||""} onChange={v=>up("stock",+v)} type="number" placeholder="0" disabled={hasVariants} /></div>
            </div>
          </Sec>

          {/* ══ VISIBILITY ══ */}
          <Sec delay={120}>
            <SLabel t="Visibility & Status" />
            {[
              {label:"Published & Live",     sub:"Visible to all shoppers",   key:"isPublished" as keyof FormState, val:form.isPublished},
              {label:"Available to Purchase",sub:"Customers can add to cart", key:"available"   as keyof FormState, val:form.available},
            ].map(row=>(
              <div key={row.key} className="vis-row">
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.white }}>{row.label}</div>
                  <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{row.sub}</div>
                </div>
                <Toggle on={row.val as boolean} onChange={v=>up(row.key,v)} />
              </div>
            ))}
          </Sec>

          {/* ══ ATTRIBUTES — full width ══ */}
          {isElec && (
            <Sec full delay={180}>
              <SLabel t="Attributes & Filters" sub="Improve search & filter discoverability" />

              {/* colours */}
              <div style={{ marginBottom:16 }}>
                <FL l="Colours" />
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {electronicsColorAttributes.values.map(c=>{
                    const on=selColors.includes(c.id);
                    return (
                      <button key={c.id} className="sw" onClick={()=>toggleColor(c.id,!on)} title={c.name}
                        style={{ background: c.gradient??c.hexCodes?.[0]??"#ccc", border: on ? `3px solid ${T.purple}` : "2px solid rgba(255,255,255,.15)", boxShadow: on ? `0 0 0 2px ${T.pageBg},0 0 0 4px ${T.purple}` : "none", transform: on ? "scale(1.18)" : "scale(1)" }} />
                    );
                  })}
                </div>
                {selColors.length>0 && <div style={{ marginTop:8, fontSize:11, color:T.muted }}>{selColors.length} colour{selColors.length>1?"s":""} selected — variant cards below ↓</div>}
              </div>

              {/* other attrs — 2-col grid on desktop */}
              <div style={{ display:"grid", gridTemplateColumns: isMob?"1fr":"1fr 1fr", gap:12 }}>
                {electronicsAttributes.filter(g=>g.id!==electronicsColorAttributes.id).map((g:ElectronicsAttributeGroup)=>{
                  const cur=form.attributes?.[g.id];
                  return (
                    <div key={g.id}>
                      <FL l={g.name} />
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {g.values.map(v=>{
                          const on=Array.isArray(cur)?cur.includes(v.id):cur===v.id;
                          return (
                            <button key={v.id} className={`ac${on?" on":""}`}
                              onClick={()=>{ const next:Record<number,number|number[]|undefined>={...form.attributes,[g.id]:on?undefined:v.id}; up("attributes",next); }}>
                              {v.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Sec>
          )}

          {/* ══ VARIANTS — full width ══ */}
          {hasVariants && (
            <Sec full delay={220}>
              <SLabel t="Variants" sub="One card per colour — upload image & set stock" />
              <div style={{ display:"grid", gridTemplateColumns: isMob?"1fr":"1fr 1fr", gap:10 }}>
                {selColors.map(cid=>(
                  <VariantCard key={cid} colorId={cid} variant={form.variants?.[cid]} stock={form.stock} onUpdate={updateVariant} onRemove={removeVariantColor} />
                ))}
              </div>
            </Sec>
          )}

          {/* ══ MEDIA — full width ══ */}
          <Sec full delay={260}>
            <SLabel t="Media" sub={`Up to ${MAX_IMAGES} product images`} />

            {/* photo grid */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:14, opacity:hasVariants?.35:1, pointerEvents:hasVariants?"none":"auto" }}>
              {form.photos.map(url=>(
                <div key={url} className="media-thumb">
                  <img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  <button onClick={()=>handleDelPhoto(url)} style={{ position:"absolute", top:5, right:5, width:20, height:20, borderRadius:"50%", background:"rgba(0,0,0,.75)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <X size={10} color="#fff" />
                  </button>
                </div>
              ))}

              {form.images.map((file,i)=>(
                <div key={i} className="media-thumb">
                  <img src={URL.createObjectURL(file)} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  <button onClick={()=>up("images",form.images.filter((_,idx)=>idx!==i))} style={{ position:"absolute", top:5, right:5, width:20, height:20, borderRadius:"50%", background:"rgba(0,0,0,.75)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <X size={10} color="#fff" />
                  </button>
                </div>
              ))}

              {(form.photos.length+form.images.length)<MAX_IMAGES && !hasVariants && (
                <label className="add-media">
                  <Camera size={18} color={T.purple} />
                  <span style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", fontWeight:700 }}>ADD PHOTO</span>
                  <input type="file" hidden accept="image/*" multiple onChange={e=>{
                    if(!e.target.files)return;
                    const ex=form.photos.length+form.images.length;
                    const files=Array.from(e.target.files).slice(0,MAX_IMAGES-ex);
                    up("images",[...form.images,...files]);
                  }} />
                </label>
              )}
            </div>

            {hasVariants && <div style={{ fontSize:11, color:T.muted, marginBottom:14 }}>Upload images per colour variant above ↑</div>}

            {/* video */}
            <div style={{ border:`1.5px dashed rgba(106,0,255,.3)`, borderRadius:14, padding:14, background:T.surface }}>
              <div style={{ fontSize:9, letterSpacing:"0.18em", color:T.muted, marginBottom:10, fontWeight:700 }}>VIDEO (OPTIONAL)</div>
              {form.videoFile ? (
                <div>
                  <video src={URL.createObjectURL(form.videoFile)} controls style={{ width:"100%", maxHeight:220, borderRadius:8 }} />
                  <button onClick={()=>up("videoFile",null)} style={{ marginTop:8, background:"none", border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:11, color:T.muted }}>Remove Video</button>
                </div>
              ): form.video ? (
                <div>
                  <video src={form.video} controls style={{ width:"100%", maxHeight:220, borderRadius:8 }} />
                  <button onClick={()=>up("video",null)} style={{ marginTop:8, background:"none", border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:11, color:T.muted }}>Replace Video</button>
                </div>
              ) : (
                <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                  <Video size={16} color={T.purple} />
                  <span style={{ fontSize:12, color:T.muted }}>Upload product video…</span>
                  <input type="file" hidden accept="video/mp4,video/quicktime" onChange={e=>{ const f=e.target.files?.[0]; if(f) up("videoFile",f); }} />
                </label>
              )}
            </div>
          </Sec>

          {/* ══ BOTTOM SAVE / DELETE — full width ══ */}
          <div className={isMob?"":"sc-full"} style={{ display:"flex", gap:12, paddingTop: isMob ? 8 : 0 }}>
            <motion.button whileTap={{scale:0.96}} disabled={!dirty||saving} onClick={handleSave}
              style={{ flex:1, padding:"15px 0", background: dirty&&!saving ? GRAD : "rgba(255,255,255,.06)", backgroundSize:"200% 100%", animation: dirty&&!saving ? "gradSpin 3s ease infinite" : "none", border:"none", borderRadius:14, color: dirty&&!saving ? "#fff" : T.mutedDim, fontWeight:800, fontSize:15, cursor: dirty&&!saving ? "pointer" : "not-allowed", boxShadow: dirty&&!saving ? "0 6px 24px rgba(106,0,255,.45)" : "none", display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontFamily:"inherit", transition:"all .2s" }}>
              {saving
                ? <div style={{ width:18,height:18,border:"2.5px solid #fff",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite" }} />
                : <><Upload size={15}/> Save Product</>
              }
            </motion.button>

            {isEdit && form.listingId && (
              <button
                onClick={()=>{ if(!window.confirm("Delete this product permanently?"))return; dispatch({type:"products/delete/fulfilled",payload:{listingId:form.listingId!}}); navigate(-1); }}
                style={{ width:52, height:52, background:"rgba(255,59,92,.1)", border:"1.5px solid rgba(255,59,92,.35)", borderRadius:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <Trash2 size={18} color={T.red} />
              </button>
            )}
          </div>

        </div>
      </div>

      <Toast visible={snack.open} msg={snack.msg} type={snack.type} />
    </div>
  );
};

export default AddProducts;